import * as React from 'react';
import { useState, useEffect, useRef, useImperativeHandle } from 'react';

import dayjs from 'dayjs';
import { darken, lighten, transparentize } from 'polished';
import styled, { css } from 'styled-components';

import { COLOR, FONT_SIZE, FONT_STACK, RADIUS, SHADOW } from '../../packages/constants';
import { ChevronLeft, ChevronRight, SortDown } from '../../packages/icons';
import { ButtonAsText } from '../button';
import { Overlay } from '../modal';

/**
 * Month Date Picker Calendar
 */
export interface MonthDatePickerCalendarProps {
    year?: number | null;
    monthIndex?: number | null;
    dispatch: any;
    dataId: string;
    mode: 'absolute' | 'fixed';
    left?: number;
    top?: number;
}

interface StyledMonthDatePickerCalendarProps {
    mode: 'absolute' | 'fixed';
    left?: number;
    top?: number;
}

const calendarHeight = 194;
const calendarPadding = 6;
const calendarMargin = 6;

const StyledMonthDatePickerCalendar = styled.div<StyledMonthDatePickerCalendarProps>`
    ${(props) => props.mode && `position: ${props.mode};`}
    ${(props) => props.left && `left: ${props.left}px;`}
    ${(props) => props.top && `top: ${props.top}px;`}
    z-index: 100;
    box-sizing: border-box;
    margin-top: ${calendarMargin}px;
    margin-bottom: ${calendarMargin}px;
    border: 1px solid ${COLOR.DROPDOWN.DROPDOWN_BORDER};
    padding: ${calendarPadding}px;
    width: 280px;
    height: ${calendarHeight}px;
    border-radius: ${RADIUS.DEFAULT};
    background: #fff;
    box-shadow: ${SHADOW.DEFAULT};
    font-family: ${FONT_STACK};
`;

const Cell = styled.li`
    box-sizing: border-box;
    display: inline-block;
    width: 25%;
    padding: 10px;
    border-radius: 100%;
    color: ${COLOR.DROPDOWN.DROPDOWN_OPTION_TEXT};
`;

interface CellButtonProps {
    isSelected?: boolean;
    isGrey?: boolean;
}

const CellButton = styled.button<CellButtonProps>`
    background: none;
    border: none;
    display: block;
    margin: 0 auto;
    border-radius: 15px;
    padding: 6px 10px;
    cursor: pointer;
    font-family: ${FONT_STACK};
    ${(props) =>
        props.isGrey &&
        css`
            color: #b7c3d6;
        `};
    &:hover {
        background: ${COLOR.DROPDOWN.DROPDOWN_OPTION_HOVER};
    }
    ${(props) =>
        props.isSelected &&
        css`
            color: #fff;
            background: ${COLOR.DROPDOWN.DROPDOWN_OPTION_SELECTED};
            &:hover {
                background: ${COLOR.DROPDOWN.DROPDOWN_OPTION_SELECTED};
            }
        `};
`;

const NavButton = styled.button`
    background: none;
    border: none;
    border-radius: 100%;
    height: 30px;
    width: 30px;
    display: inline-block;
    cursor: pointer;

    &:hover {
        background: ${COLOR.DROPDOWN.DROPDOWN_OPTION_HOVER};
    }
    &:active {
        background: ${darken('0.1', COLOR.DROPDOWN.DROPDOWN_OPTION_HOVER)};
    }
`;

const PrevButton = styled(NavButton)``;

const NextButton = styled(NavButton)``;

const StyledChevronLeft = styled(ChevronLeft)`
    margin: 0 auto;
`;

const StyledChevronRight = styled(ChevronRight)`
    margin: 0 auto;
`;

const YearButton = styled(ButtonAsText)`
    display: inline-block;
    margin: 0 auto;
    position: relative;
    top: 1px;
    align-self: center;
    font-weight: 400;
    cursor: pointer;
`;

const YearRangeHeading = styled.span`
    margin: 0 auto;
    font-family: ${FONT_STACK};
    font-size: ${FONT_SIZE.SMALL};
    align-self: center;
`;

const CalendarHeader = styled.div`
    display: flex;
`;

const MonthsList = styled.ol`
    display: block;
`;

const YearsList = styled(MonthsList)``;

enum ViewModes {
    Month = 'MONTH',
    Year = 'YEAR',
}

const Years = ({
    decadeStart,
    clickedYear,
    selectedYear,
    dataId,
}: {
    decadeStart: number;
    clickedYear: any;
    selectedYear: number | null;
    dataId: string;
}) => {
    /**
     * should be the decade for the year user has navigated to
     * e.g. 2020-2029, then have a year either side
     */
    const yearOptions = (() => {
        const arr = [];
        for (let i = -1; i < 11; i++) {
            arr.push(decadeStart + i);
        }
        return arr;
    })();
    const decadeEnd = decadeStart + 9;

    return (
        <YearsList data-test-id={`${dataId}__years`}>
            {yearOptions.map((item) => {
                const isSelected = typeof selectedYear === 'number' && item === selectedYear;
                return (
                    <Cell key={`years-${item}`}>
                        <CellButton
                            onClick={() => clickedYear(item)}
                            isGrey={item < decadeStart || item > decadeEnd}
                            isSelected={isSelected}
                            data-test-id={`${dataId}__years__button-year-${item}`}
                            data-selected={isSelected}
                            type="button"
                        >
                            {item}
                        </CellButton>
                    </Cell>
                );
            })}
        </YearsList>
    );
};

const Months = ({
    clickedMonth,
    selectedMonth,
    isSelectedYear,
    dataId,
}: {
    clickedMonth: any;
    selectedMonth: number | null;
    isSelectedYear: boolean;
    dataId: string;
}) => {
    const monthOptions = [];

    for (let i = 0; i < 12; i++) {
        monthOptions.push(dayjs().month(i).format('MMM'));
    }

    return (
        <MonthsList data-test-id={`${dataId}__months`}>
            {monthOptions.map((monthName, i) => {
                const isSelected = typeof selectedMonth === 'number' && i === selectedMonth && isSelectedYear;
                return (
                    <Cell key={`months-${i}`}>
                        <CellButton
                            autoFocus={isSelected}
                            onClick={() => clickedMonth(i)}
                            isSelected={isSelected}
                            data-test-id={`${dataId}__months__button-month-${i}`}
                            data-selected={isSelected}
                            type="button"
                        >
                            {monthName}
                        </CellButton>
                    </Cell>
                );
            })}
        </MonthsList>
    );
};

const endash = '\u2013';

export const MonthDatePickerCalendar = React.forwardRef<HTMLDivElement, MonthDatePickerCalendarProps>(
    ({ year = null, monthIndex = null, dispatch, dataId, ...props }, ref) => {
        /**
         * The values
         */
        const [selectedYear, setSelectedYear] = useState(year);
        const [selectedMonth, setSelectedMonth] = useState(monthIndex);
        /**
         * Navigation
         */
        const [navYear, setNavYear] = useState(selectedYear || dayjs().year());
        const [navDecadeStart, setNavDecadeStart] = useState(Math.floor(navYear / 10) * 10);
        const [viewMode, setViewMode] = useState(ViewModes.Month);

        const clickedYear = (year: number) => {
            setNavYear(year);
            setNavDecadeStart(Math.floor(year / 10) * 10);
            setSelectedMonth(null); // clear the month when a year is chosen
            setViewMode(ViewModes.Month);
        };

        const clickedMonth = (monthIndex: number) => {
            setSelectedMonth(monthIndex);
            setSelectedYear(navYear);
            dispatch({ type: 'SELECTED_MONTH', data: { monthIndex, year: navYear } });
        };

        const clickedPrev = () => {
            const newYear = (() => {
                if (viewMode === ViewModes.Year) {
                    return navYear - 10;
                }
                return navYear - 1;
            })();

            setNavDecadeStart(Math.floor(newYear / 10) * 10);
            setNavYear(newYear);
        };

        const clickedNext = () => {
            const newYear = (() => {
                if (viewMode === ViewModes.Year) {
                    return navYear + 10;
                }
                return navYear + 1;
            })();

            setNavDecadeStart(Math.floor(newYear / 10) * 10);
            setNavYear(newYear);
        };

        return (
            <StyledMonthDatePickerCalendar ref={ref} data-test-id={dataId} {...props}>
                <CalendarHeader>
                    <PrevButton onClick={clickedPrev} data-test-id={`${dataId}__button-prev`} type="button">
                        <StyledChevronLeft title="Previous" width={'8px'} height={'8px'} fill={COLOR.MAIN.DARK_GREY} />
                    </PrevButton>
                    {viewMode === ViewModes.Month && (
                        <YearButton
                            dataId={`${dataId}__button-active-year`}
                            onClick={() => setViewMode(ViewModes.Year)}
                            size={FONT_SIZE.SMALL}
                            type="button"
                        >
                            {navYear}
                        </YearButton>
                    )}
                    {viewMode === ViewModes.Year && (
                        <YearRangeHeading data-test-id={`${dataId}__years-range`}>{`${navDecadeStart} ${endash} ${
                            navDecadeStart + 9
                        }`}</YearRangeHeading>
                    )}
                    <NextButton onClick={clickedNext} data-test-id={`${dataId}__button-next`} type="button">
                        <StyledChevronRight title="Next" width={'8px'} height={'8px'} fill={COLOR.MAIN.DARK_GREY} />
                    </NextButton>
                </CalendarHeader>
                {viewMode === ViewModes.Month && (
                    <Months
                        dataId={dataId}
                        clickedMonth={clickedMonth}
                        selectedMonth={selectedMonth}
                        isSelectedYear={selectedYear === navYear}
                    />
                )}
                {viewMode === ViewModes.Year && (
                    <Years
                        dataId={dataId}
                        decadeStart={navDecadeStart}
                        clickedYear={clickedYear}
                        selectedYear={selectedYear}
                    />
                )}
            </StyledMonthDatePickerCalendar>
        );
    },
);
MonthDatePickerCalendar.displayName = 'MonthDatePickerCalendar';

/**
 * Month Date Picker
 */

export interface MonthDatePickerProps {
    value?: string;
    dataId: string;
    onChange?: ({ year, monthIndex }: { year: number; monthIndex: number }) => void;
    /**
     * Is it disabled?
     */
    disabled?: boolean;
    invalid?: boolean;
    id?: string;
    /**
     *  placeholder text to display when there is no value
     */
    placeholder?: string;
    initialYearView?: number;
    className?: string;
    name?: string;
    position?: 'fixed' | 'absolute';
    ariaLabel?: string;
}
interface MonthDatePickerState {
    year: number;
    monthIndex: number;
    isOpen: boolean;
    initialYearView?: number;
    left?: number;
    top?: number;
}

interface DatePickerButton {
    isOpen: boolean;
    invalid?: boolean;
}

const StyledDatePickerButton = styled.button<DatePickerButton>`
    position: relative;
    z-index: ${(props) => (props.isOpen ? 100 : 1)};
    border: 1px solid;
    border-color: ${(props) => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.FORM_FIELD)};
    border-radius: ${RADIUS.FORM_FIELD};
    background-color: ${COLOR.PRIMARY.WHITE};
    color: ${COLOR.PRIMARY.BLACK};
    width: 140px;
    height: 30px;
    cursor: pointer;
    padding: 8px;
    min-width: 120px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    :disabled {
        background-color: ${darken('0.1', COLOR.PRIMARY.WHITE)};
        color: ${transparentize('0.5', COLOR.PRIMARY.BLACK)};
    }
`;

const StyledPlaceholderText = styled.span`
    color: ${lighten('0.2', COLOR.MAIN.FORM_FIELD_DARK)};
`;

const StyledDatePickerWrapper = styled.div``;

const StyledOverlay = styled(Overlay)`
    background-color: transparent;
    z-index: 90;
`;

const MonthDatePickerReducer = (state: MonthDatePickerState, action: any) => {
    switch (action.type) {
        case 'OPEN':
            return {
                ...state,
                left: action.data.left,
                top: action.data.top,
                isOpen: true,
            };
        case 'CLOSE':
            return {
                ...state,
                isOpen: false,
            };
        case 'SELECTED_MONTH':
            return {
                ...state,
                monthIndex: action.data.monthIndex,
                year: action.data.year,
                isOpen: false,
            };
        case 'UPDATE_DATE':
            return {
                ...state,
                monthIndex: action.data.monthIndex,
                year: action.data.year,
            };
        default:
            return state;
    }
};
interface UseEscapeListenerFn {
    onEsc: () => void;
}

const useEscapeListener = ({ onEsc }: UseEscapeListenerFn) => {
    useEffect(() => {
        function handleKeyListener(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                onEsc();
            }
        }

        document.addEventListener('keydown', handleKeyListener);

        return function cleanupKeyListener() {
            document.removeEventListener('keydown', handleKeyListener);
        };
    }, [onEsc]);
};

export const MonthDatePicker = React.forwardRef<HTMLButtonElement, MonthDatePickerProps>(
    (
        {
            value = '',
            dataId,
            name,
            onChange,
            placeholder = 'Select date',
            initialYearView,
            disabled,
            invalid,
            position = 'absolute',
            ariaLabel,
            ...props
        },
        ref,
    ) => {
        const [state, dispatch] = React.useReducer(MonthDatePickerReducer, {
            monthIndex: value ? dayjs(value).month() : undefined,
            year: value ? dayjs(value).year() : undefined,
            initialYearView: initialYearView,
            isOpen: false,
            left: undefined,
            top: undefined,
        });
        const { year, monthIndex } = state;

        const calendarRef = useRef<HTMLDivElement>(null);
        const buttonRef = useRef<HTMLButtonElement>(null);
        useImperativeHandle<HTMLButtonElement | null, HTMLButtonElement | null>(ref, () => buttonRef.current);

        const open = (left?: number, top?: number) => {
            dispatch({ type: 'OPEN', data: { left: left, top: top } });
        };

        const close = () => {
            buttonRef?.current?.focus();

            dispatch({ type: 'CLOSE' });
        };

        const onEsc = () => {
            close();
        };

        useEffect(() => {
            if (position === 'fixed') {
                window.addEventListener('resize', close);

                return function cleanupEventListeners() {
                    window.removeEventListener('resize', close);
                };
            }
        });

        useEffect(() => {
            /* when the value prop changes, update the state by dispatching an action */
            // ensure there is a value and that the value has both a year and month, otherwise clear the dates
            const dateRegex = new RegExp(/^\d*[-|/]\d/);
            if (!value || !value.match(dateRegex)) {
                dispatch({ type: 'UPDATE_DATE', data: { year: undefined, monthIndex: undefined } });
                return;
            }
            const date = dayjs(value);
            const year = date.year();
            const month = date.month();
            dispatch({ type: 'UPDATE_DATE', data: { year: year, monthIndex: month } });
        }, [value]);

        useEffect(() => {
            if (typeof onChange === 'function') {
                onChange({ year: state.year, monthIndex: state.monthIndex });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [state.year, state.monthIndex]);

        useEscapeListener({ onEsc });

        const getCalendarPosition = (): { left: number | undefined; top: number | undefined } => {
            let left: number | undefined;
            let top: number | undefined;

            // Only get position if the calendar is fixed
            if (position === 'fixed' && buttonRef?.current) {
                const buttonBounds = buttonRef.current.getBoundingClientRect();

                const calendarOuterHeight = calendarHeight + calendarPadding + calendarMargin;
                const calendarFitsBelow = buttonBounds.bottom + calendarOuterHeight < window.innerHeight;
                const calendarFitsAbove = buttonBounds.top - calendarOuterHeight > 0;

                // If calendar fits above but not below button, put it above.
                if (!calendarFitsBelow && calendarFitsAbove) {
                    top = buttonBounds.top - calendarOuterHeight;
                }
                // By default, put the calendar below
                else {
                    top = buttonBounds.bottom;
                }

                left = buttonBounds.left;
            }

            return {
                left: left,
                top: top,
            };
        };

        const toggle = () => {
            if (state.isOpen) {
                close();
            } else {
                const calendarPosition = getCalendarPosition();
                open(calendarPosition.left, calendarPosition.top);
            }
        };

        const buttonText = (() => {
            if (typeof monthIndex === 'number' && typeof year === 'number') {
                const monthName = dayjs().set('month', monthIndex).format('MMM');
                return `${monthName} ${endash} ${year}`;
            }
            return <StyledPlaceholderText>{placeholder}</StyledPlaceholderText>;
        })();

        return (
            <StyledDatePickerWrapper data-test-id={`${dataId}`} {...props}>
                <input type="hidden" {...(name ? { name: name } : {})} value={value} />
                <StyledDatePickerButton
                    data-test-id={`${dataId}__button-open`}
                    onClick={toggle}
                    type="button"
                    disabled={disabled}
                    invalid={invalid}
                    // @ts-ignore - TODO: Upgrade to Mantine 7
                    placeholder={placeholder}
                    isOpen={state.isOpen}
                    aria-label={ariaLabel}
                    ref={buttonRef}
                >
                    {buttonText} <SortDown fill={COLOR.MAIN.DARK_GREY} width={'6px'} height={'4px'} />
                </StyledDatePickerButton>
                {state.isOpen && (
                    <>
                        <StyledOverlay
                            onClick={() => dispatch({ type: 'CLOSE' })}
                            data-test-id={`${dataId}__overlay`}
                        />
                        <MonthDatePickerCalendar
                            dataId={`${dataId}__calendar`}
                            year={year || initialYearView}
                            monthIndex={monthIndex}
                            dispatch={dispatch}
                            mode={position}
                            left={state.left}
                            top={state.top}
                            ref={calendarRef}
                        />
                    </>
                )}
            </StyledDatePickerWrapper>
        );
    },
);

MonthDatePicker.displayName = 'MonthDatePicker';
